/**
 * Tipos e chamadas da venda condicional (consignação de balcão): o cliente
 * leva a peça pra casa pra decidir, sem venda fechada ainda.
 *
 * O contrato espelha `api/internal/modulos/condicional/dominio.go`. Mesmo
 * estilo de `vendas.ts` e `clientes.ts` — dinheiro em `Centavos`, o `sku` que
 * atravessa a fronteira é o CÓDIGO DE BARRAS, e a identidade que aparece na
 * tela é o `codigo` (`CN0001`), nunca um uuid (CONSTITUICAO.md §2).
 *
 * FATURAR não mora aqui. É `POST /vendas` com `condicional` preenchido — o
 * MESMO endpoint e o mesmo fluxo de pagamento do PDV — e por isso a função
 * `faturarCondicional` vive em `plataforma/vendas.ts`, ao lado de
 * `registrarVenda` e dos tipos de pagamento que ela reaproveita. Este arquivo
 * cobre só o que É do módulo `condicional`: abrir, listar, ver a ficha,
 * devolver e cancelar.
 */
import { api } from './api'
import type { Centavos } from './dinheiro'

/** Os quatro estados finais/inicial da condicional — mesmo `check` da 0024. */
export type StatusCondicional = 'aberta' | 'faturada' | 'devolvida' | 'cancelada'

/** Mesmo shape de `vendas.ClienteResumo` — o backend duplica de propósito
 *  (módulo não importa módulo), e o front espelha por simetria. */
export type ClienteResumo = {
  codigo: string
  nome: string
}

export type SKUResumo = {
  codigo_barras: string
  produto: string
  cor: string
  tamanho: string
}

export type ItemCondicional = {
  numero_item: number
  sku: SKUResumo
  quantidade: number
  preco_unitario_centavos: Centavos
  total_centavos: Centavos
}

/** Uma linha do carrinho no corpo de POST /condicionais — sem preço: a
 *  condicional não negocia desconto, o preço é sempre o de tabela. */
export type ItemPedidoCondicional = {
  sku: string
  quantidade: number
}

export type PedidoCondicional = {
  /** Código `C0001`. OBRIGATÓRIO — ao contrário da venda, que aceita balcão
   *  anônimo: sem cliente identificado não há crediário para conferir, e é o
   *  cliente quem volta para faturar ou devolver. */
  cliente: string
  itens: ItemPedidoCondicional[]
  /** `aaaa-mm-dd`. Combinado com o cliente; não trava nada sozinho. */
  prazo?: string
  observacao?: string
}

/** O corpo de POST /condicionais/{codigo}/devolver e .../cancelar. */
export type PedidoEncerramento = {
  motivo?: string
}

export type Condicional = {
  codigo: string
  cliente: ClienteResumo
  vendedor?: string
  status: StatusCondicional
  status_rotulo: string
  subtotal_centavos: Centavos
  prazo?: string
  observacao?: string
  itens: ItemCondicional[]
  /** `V0001` — só quando `status === 'faturada'`. */
  venda_codigo?: string
  faturada_em?: string
  devolvida_em?: string
  motivo_devolucao?: string
  cancelada_em?: string
  motivo_cancelamento?: string
  criado_em: string
}

/** Uma linha de GET /condicionais. */
export type LinhaCondicional = {
  codigo: string
  cliente: ClienteResumo
  vendedor?: string
  status: StatusCondicional
  status_rotulo: string
  criado_em: string
  itens: number
  pecas: number
  subtotal_centavos: Centavos
  prazo?: string
}

/** O chip "Todas" soma ao status de verdade — a API não tem status `todas`,
 *  é só o front que não manda o parâmetro nesse caso. */
export type FiltroStatus = 'todas' | StatusCondicional

export type ListaDeCondicionais = {
  linhas: LinhaCondicional[]
  total: number
  pagina: number
  tamanho: number
  paginas: number
}

// ---------------------------------------------------------------- chamadas

export function abrirCondicional(pedido: PedidoCondicional): Promise<Condicional> {
  return api.post<Condicional>('/condicionais', pedido)
}

export function carregarCondicionais(
  parametros: {
    cliente?: string
    status?: FiltroStatus
    busca?: string
    pagina?: number
    tamanho?: number
  },
  sinal?: AbortSignal,
): Promise<ListaDeCondicionais> {
  const params = new URLSearchParams()
  if (parametros.cliente) params.set('cliente', parametros.cliente)
  if (parametros.status && parametros.status !== 'todas') params.set('status', parametros.status)
  if (parametros.busca) params.set('busca', parametros.busca)
  if (parametros.pagina) params.set('pagina', String(parametros.pagina))
  if (parametros.tamanho) params.set('tamanho', String(parametros.tamanho))
  return api.get<ListaDeCondicionais>(`/condicionais?${params}`, sinal)
}

export function carregarCondicional(codigo: string, sinal?: AbortSignal): Promise<Condicional> {
  return api.get<Condicional>(`/condicionais/${encodeURIComponent(codigo)}`, sinal)
}

/** Devolvida: o cliente trouxe a peça de volta depois de decidir não comprar.
 *  Motivo é OPCIONAL (até 500 caracteres). */
export function devolverCondicional(codigo: string, motivo?: string): Promise<Condicional> {
  const corpo: PedidoEncerramento = {}
  const limpo = motivo?.trim()
  if (limpo) corpo.motivo = limpo
  return api.post<Condicional>(`/condicionais/${encodeURIComponent(codigo)}/devolver`, corpo)
}

/** Cancelada: erro operacional, antes de qualquer decisão do cliente. Motivo
 *  é OBRIGATÓRIO (3 a 500 caracteres) — o servidor recusa com 422 em
 *  `campos.motivo` se faltar ou for curto. */
export function cancelarCondicional(codigo: string, motivo: string): Promise<Condicional> {
  return api.post<Condicional>(`/condicionais/${encodeURIComponent(codigo)}/cancelar`, {
    motivo: motivo.trim(),
  })
}

// -------------------------------------------------------------- catálogo

/** Os chips da listagem, na ordem do protótipo mais próximo (Clientes). */
export const FILTROS_STATUS: { chave: FiltroStatus; rotulo: string }[] = [
  { chave: 'todas', rotulo: 'Todas' },
  { chave: 'aberta', rotulo: 'Abertas' },
  { chave: 'faturada', rotulo: 'Faturadas' },
  { chave: 'devolvida', rotulo: 'Devolvidas' },
  { chave: 'cancelada', rotulo: 'Canceladas' },
]

/** Limites de texto — os mesmos `check` da migração 0024. */
export const TAMANHO_MAXIMO_OBSERVACAO = 500
export const TAMANHO_MAXIMO_MOTIVO_DEVOLUCAO = 500
export const TAMANHO_MINIMO_MOTIVO_CANCELAMENTO = 3
export const TAMANHO_MAXIMO_MOTIVO_CANCELAMENTO = 500
